/**
 * Functions that directly interact with the Facebook/Instagram Graph API.
 *
 * https://developers.facebook.com/docs/graph-api/overview/
 */

import * as Sentry from '@sentry/vue';
import i18n from '@/i18n';

import {deepCopyObject} from './utils';
import bus from './bus';

const FACEBOOK_LOGIN_PERMISSIONS = [
  'pages_read_engagement',
  'pages_show_list',
  'pages_manage_posts',
  'business_management', // Required to also list pages connected to business account, see https://developers.facebook.com/docs/graph-api/changelog/version17.0#user-accounts
  'instagram_basic', // Required to get instagram_business_account.
  'instagram_content_publish',
];

const FACEBOOK_SESSION_LOGGED_IN_KEY = 'msr:facebook-session-logged-in';

/**
 * Call the Facebook SDK to retrieve a User access token.
 *
 * We rely on the SDK being initialized here and available as 'FB'.
 * If the User has not yet connected and logged into his Facebook
 * account he is automatically prompted to do so in a dialog (asking
 * for page permissions etc.). The Facebook SDK generates all the
 * necessary UI for that.
 *
 * Note: We try getLoginStatus() first as it is using a cached
 *   response if available and only call login() if that didn't work.
 *
 * Returns:
 *   Promise: Return the user access token as String.
 */
function getFacebookUserAccessToken() {
  if (typeof FB === 'undefined') {
    bus.$emit('alert-error', {
      message: 'facebookSdkCouldNotBeInitialized',
      translate: true,
      timeout: 0,
    });
    return;
  }

  const tokenPromise = new Promise((resolve, reject) => {
    const permissionsScope = FACEBOOK_LOGIN_PERMISSIONS.join(',');
    const reauthenticate =
      sessionStorage.getItem(FACEBOOK_SESSION_LOGGED_IN_KEY) !== 'true';

    const loginOptions = {
      scope: permissionsScope,
      return_scopes: true,
    };
    if (reauthenticate) {
      loginOptions.auth_type = 'reauthenticate';
    }
    console.log('[DEBUG] FB login options', loginOptions);

    FB.login((response) => {
      Sentry.captureMessage('Logged into facebook', {
        extra: {
          response: JSON.stringify({response: response}),
        },
        level: 'info',
      });
      sessionStorage.setItem(FACEBOOK_SESSION_LOGGED_IN_KEY, 'true');

      if (!response.authResponse) {
        console.warn(
          '[WARN] Facebook login failed, user may have cancelled it'
        );
        Sentry.captureMessage(
          'Facebook login failed, user may have cancelled it',
          {
            level: 'error',
          }
        );
        return reject();
      }

      // For some reason even with `return_scopes: true` the login's
      // authResponse may not contain a grantedScopes list to validate.
      // We'll do an extra API call to /me/permissions to log and
      // validate permissions.
      getFacebookUserPermissions().then((permissions) => {
        let allPermissionsGranted = false;
        if (permissions !== undefined) {
          allPermissionsGranted = permissions.every(
            (perm) => perm.status == 'granted'
          );
        }

        console.log('[DEBUG] Facebook permissions:', permissions);
        Sentry.captureMessage('Facebook permissions fetched', {
          extra: {
            permissions: JSON.stringify(permissions),
            valid: allPermissionsGranted,
          },
          level: allPermissionsGranted ? 'info' : 'error',
        });

        if (!allPermissionsGranted) {
          const warning = i18n.t('facebookNotAllPermissionsGranted');
          console.info('[INFO] Facebook login insufficient: ' + warning);
          alert(warning);
          logoutFacebook();
          return reject();
        } else {
          console.info('[INFO] Facebook login successful');
          resolve(response.authResponse.accessToken);
        }
      });
    }, loginOptions);
  });
  return tokenPromise;
}

/**
 * Return list of app permissions and if granted or declined.
 *
 * Example response:
 *
 *   [
 *     {
 *       "permission": "pages_show_list",
 *       "status": "granted"
 *     },
 *     {
 *       "permission": "instagram_content_publish",
 *       "status": "granted"
 *     },
 *     {
 *       "permission": "public_profile",
 *       "status": "granted"
 *     },
 *     {
 *       "permission": "instagram_basic",
 *       "status": "declined"
 *     },
 *     {
 *       "permission": "pages_manage_posts",
 *       "status": "declined"
 *     },
 *     {
 *       "permission": "pages_read_engagement",
 *       "status": "declined"
 *     }
 *   ]
 */
function getFacebookUserPermissions() {
  return new Promise((resolve, reject) => {
    FB.api('/me/permissions', (response) => {
      resolve(response.data);
    });
  });
}

function getFacebookUserName() {
  return new Promise((resolve, reject) => {
    FB.api('/me', (response) => {
      resolve(response.name);
    });
  });
}

/**
 * Return a list of pages the user has a role on.
 *
 * Assumes we are already logged in via the Facebook SDK. The response
 * will also include locations of a Page if any exist: These are
 * basically sub-pages that we'll also have to allow posting to.
 *
 * Returns:
 *   Promise
 *
 * Example API response from which the outmost 'data' part aka the
 *   list of pages is returned:
 *
 * {
 *   "data": [
 *     {
 *       "name": "Myshopradio Test",
 *       "link": "https://www.facebook.com/108867494164437",
 *       "photos": {
 *         "data": [
 *           {
 *             "images": [
 *               {
 *                 "height": 443,
 *                 "source": "https://scontent-dus1-1.xx.fbcdn.net/v/t1.0-9/97202251_108877190830134_6750701704611627008_n.jpg?_nc_cat=105&_nc_sid=85a577&_nc_ohc=eR5SOLRi2dMAX976tWW&_nc_ht=scontent-dus1-1.xx&oh=2aa8fe1ae5ed3345de9c9cad7e5bf9c6&oe=5EE7214A",
 *                 "width": 443
 *               },
 *               {
 *                 "height": 320,
 *                 "source": "https://scontent-dus1-1.xx.fbcdn.net/v/t1.0-0/p320x320/97202251_108877190830134_6750701704611627008_n.jpg?_nc_cat=105&_nc_sid=85a577&_nc_ohc=eR5SOLRi2dMAX976tWW&_nc_ht=scontent-dus1-1.xx&_nc_tp=6&oh=de02c033a16422eb0d3a0dfad8021cc6&oe=5EEA0729",
 *                 "width": 320
 *               },
 *               {
 *                 "height": 130,
 *                 "source": "https://scontent-dus1-1.xx.fbcdn.net/v/t1.0-0/p130x130/97202251_108877190830134_6750701704611627008_n.jpg?_nc_cat=105&_nc_sid=85a577&_nc_ohc=eR5SOLRi2dMAX976tWW&_nc_ht=scontent-dus1-1.xx&_nc_tp=6&oh=18136a430b3df53eb6731d32ba60947d&oe=5EE7E948",
 *                 "width": 130
 *               },
 *               {
 *                 "height": 225,
 *                 "source": "https://scontent-dus1-1.xx.fbcdn.net/v/t1.0-0/p75x225/97202251_108877190830134_6750701704611627008_n.jpg?_nc_cat=105&_nc_sid=85a577&_nc_ohc=eR5SOLRi2dMAX976tWW&_nc_ht=scontent-dus1-1.xx&_nc_tp=6&oh=889e35d8e5fae096869f6b1bb150a7ae&oe=5EE69F34",
 *                 "width": 225
 *               }
 *             ],
 *             "created_time": "2020-05-15T12:50:31+0000",
 *             "id": "108877187496801"
 *           },
 *           {
 *             "images": [
 *               {
 *                 "height": 1290,
 *                 "source": "https://scontent-dus1-1.xx.fbcdn.net/v/t1.0-9/98175369_108867594164427_167975135859441664_o.png?_nc_cat=103&_nc_sid=7aed08&_nc_ohc=NVEu6ZY3-b8AX9metCY&_nc_ht=scontent-dus1-1.xx&oh=8c7d58418f77603955bd2943930eeafe&oe=5EE6F862",
 *                 "width": 1290
 *               },
 *               {
 *                 "height": 960,
 *                 "source": "https://scontent-dus1-1.xx.fbcdn.net/v/t1.0-9/p960x960/98175369_108867594164427_167975135859441664_o.png?_nc_cat=103&_nc_sid=7aed08&_nc_ohc=NVEu6ZY3-b8AX9metCY&_nc_ht=scontent-dus1-1.xx&oh=fc65368bf05f93c14de1488ae3125e79&oe=5EE8588D",
 *                 "width": 960
 *               },
 *               {
 *                 "height": 720,
 *                 "source": "https://scontent-dus1-1.xx.fbcdn.net/v/t1.0-9/p720x720/98175369_108867594164427_167975135859441664_o.png?_nc_cat=103&_nc_sid=7aed08&_nc_ohc=NVEu6ZY3-b8AX9metCY&_nc_ht=scontent-dus1-1.xx&oh=2e6e9775e0ef7e39de4099bc834c3edc&oe=5EE8B411",
 *                 "width": 720
 *               },
 *               {
 *                 "height": 600,
 *                 "source": "https://scontent-dus1-1.xx.fbcdn.net/v/t1.0-0/p600x600/98175369_108867594164427_167975135859441664_o.png?_nc_cat=103&_nc_sid=7aed08&_nc_ohc=NVEu6ZY3-b8AX9metCY&_nc_ht=scontent-dus1-1.xx&oh=a2179fe6733c04d53873c22027340ca4&oe=5EE94AB4",
 *                 "width": 600
 *               },
 *               {
 *                 "height": 480,
 *                 "source": "https://scontent-dus1-1.xx.fbcdn.net/v/t1.0-0/p480x480/98175369_108867594164427_167975135859441664_o.png?_nc_cat=103&_nc_sid=7aed08&_nc_ohc=NVEu6ZY3-b8AX9metCY&_nc_ht=scontent-dus1-1.xx&oh=75c3b6eee366120a679abb01719773aa&oe=5EE7912C",
 *                 "width": 480
 *               },
 *               {
 *                 "height": 320,
 *                 "source": "https://scontent-dus1-1.xx.fbcdn.net/v/t1.0-0/p320x320/98175369_108867594164427_167975135859441664_o.png?_nc_cat=103&_nc_sid=7aed08&_nc_ohc=NVEu6ZY3-b8AX9metCY&_nc_ht=scontent-dus1-1.xx&oh=4a04bad4dec851e6812ee169526dadfb&oe=5EE67A78",
 *                 "width": 320
 *               },
 *               {
 *                 "height": 540,
 *                 "source": "https://scontent-dus1-1.xx.fbcdn.net/v/t1.0-0/p180x540/98175369_108867594164427_167975135859441664_o.png?_nc_cat=103&_nc_sid=7aed08&_nc_ohc=NVEu6ZY3-b8AX9metCY&_nc_ht=scontent-dus1-1.xx&oh=c776a855154809a2d3e8c41fd40e7eed&oe=5EE99282",
 *                 "width": 540
 *               },
 *               {
 *                 "height": 130,
 *                 "source": "https://scontent-dus1-1.xx.fbcdn.net/v/t1.0-0/p130x130/98175369_108867594164427_167975135859441664_o.png?_nc_cat=103&_nc_sid=7aed08&_nc_ohc=NVEu6ZY3-b8AX9metCY&_nc_ht=scontent-dus1-1.xx&oh=0a4cf7deb33800d8e15a616058e67948&oe=5EE845E8",
 *                 "width": 130
 *               },
 *               {
 *                 "height": 225,
 *                 "source": "https://scontent-dus1-1.xx.fbcdn.net/v/t1.0-0/p75x225/98175369_108867594164427_167975135859441664_o.png?_nc_cat=103&_nc_sid=7aed08&_nc_ohc=NVEu6ZY3-b8AX9metCY&_nc_ht=scontent-dus1-1.xx&oh=9c41e3eb646e7feb08e75e579b7044cc&oe=5EE71FD6",
 *                 "width": 225
 *               }
 *             ],
 *             "created_time": "2020-05-15T12:39:19+0000",
 *             "id": "108867587497761"
 *           }
 *         ],
 *         "paging": {
 *           "cursors": {
 *             "before": "QVFIUmtKdk5reGJDMUd0SkJ0ZAFJ0eTMyd0RhdjF6TFJNMUZAlNkh5Ujg1ZA0QtSTNvZA3ZAsQUROTWd0aEY1b0ExcEcyVHRWQTF4aERvS3RFUXVCeDM2MldRR09n",
 *             "after": "QVFIUkMzdlhtOUZAYU3l1QkVUbFBfQktib3BzRkl1djdyVUt5ODVFMVo2bWZA3LXZAMWVpQWGtaSmJmMmZAwY1o5SnN5RHhIUFZApYnBJSkk4ckJqSF9EZAnBZAZAkNR"
 *           }
 *         }
 *       },
 *       "locations": {
 *         "data": [
 *           {
 *             "name": "Myshopradio Test",
 *             "link": "https://www.facebook.com/109626330758311",
 *             "photos": {
 *               "data": [
 *                 {
 *                   "images": [
 *                     {
 *                       "height": 410,
 *                       "source": "https://scontent-dus1-1.xx.fbcdn.net/v/t1.0-9/97712659_109627990758145_8068776749132939264_n.png?_nc_cat=104&_nc_sid=85a577&_nc_ohc=rpURSqllgMAAX9MMESY&_nc_ht=scontent-dus1-1.xx&oh=e921dca0ab5a6dc35e52f5d43266f0fc&oe=5EEA0A6B",
 *                       "width": 410
 *                     },
 *                     {
 *                       "height": 320,
 *                       "source": "https://scontent-dus1-1.xx.fbcdn.net/v/t1.0-0/p320x320/97712659_109627990758145_8068776749132939264_n.png?_nc_cat=104&_nc_sid=85a577&_nc_ohc=rpURSqllgMAAX9MMESY&_nc_ht=scontent-dus1-1.xx&oh=e8c84406e2dd6c2097ffde454af5ecd2&oe=5EE6508C",
 *                       "width": 320
 *                     },
 *                     {
 *                       "height": 130,
 *                       "source": "https://scontent-dus1-1.xx.fbcdn.net/v/t1.0-0/p130x130/97712659_109627990758145_8068776749132939264_n.png?_nc_cat=104&_nc_sid=85a577&_nc_ohc=rpURSqllgMAAX9MMESY&_nc_ht=scontent-dus1-1.xx&oh=b52defb7cb0eefb3d219f5c86d18e792&oe=5EE9F1E5",
 *                       "width": 130
 *                     },
 *                     {
 *                       "height": 225,
 *                       "source": "https://scontent-dus1-1.xx.fbcdn.net/v/t1.0-0/p75x225/97712659_109627990758145_8068776749132939264_n.png?_nc_cat=104&_nc_sid=85a577&_nc_ohc=rpURSqllgMAAX9MMESY&_nc_ht=scontent-dus1-1.xx&oh=7772c44f8f6f60771382bbbd979ede7c&oe=5EE96791",
 *                       "width": 225
 *                     }
 *                   ],
 *                   "created_time": "2020-05-18T12:03:47+0000",
 *                   "id": "109627987424812"
 *                 }
 *               ],
 *               "paging": {
 *                 "cursors": {
 *                   "before": "QVFIUjlNODhrWWc0Ni0yZAzhFYmxKRVBsQUhBM0xrLUNYZAFBYUXB0WDhFZAG8tQVI4RUczS3c1TDZAPdnZAiYUtBaXhSeFZAxek5aOEZAobG5BSEhGS2M1QXdaYzhn",
 *                   "after": "QVFIUjlNODhrWWc0Ni0yZAzhFYmxKRVBsQUhBM0xrLUNYZAFBYUXB0WDhFZAG8tQVI4RUczS3c1TDZAPdnZAiYUtBaXhSeFZAxek5aOEZAobG5BSEhGS2M1QXdaYzhn"
 *                 }
 *               }
 *             },
 *             "location": {
 *               "city": "Bielefeld",
 *               "country": "Germany",
 *               "latitude": 52.02484,
 *               "longitude": 8.53004,
 *               "street": "Arndtstrasse 6-8",
 *               "zip": "33602"
 *             },
 *             "access_token": "EAAwph5aXNvMBAHjJOIsT51ZB8mu1Gd1WjTWlwrBexzZCd9adKaY04b2Id3Q8S1B9riagMBDZCVedzsbgL02LEkrq3B6yoAKMoJwPB3rtIrmkWF8MoR6ZArvTYqzyXr5yrZATGajdo97lKITy79hSbzx1wS44tZAyujTMKpt5m8vMqPxDiZB7F3PSpeWpwZD",
 *             "id": "109626330758311"
 *           },
 *           {
 *             "name": "Myshopradio Test",
 *             "link": "https://www.facebook.com/101481864916163",
 *             "photos": {
 *               "data": [
 *                 {
 *                   "images": [
 *                     {
 *                       "height": 410,
 *                       "source": "https://scontent-dus1-1.xx.fbcdn.net/v/t1.0-9/98174169_101486681582348_7512797377309179904_n.png?_nc_cat=103&_nc_sid=85a577&_nc_ohc=HlIA8UQWDc4AX_zwgiY&_nc_ht=scontent-dus1-1.xx&oh=86559a6d12066b49bc52d32b5e35861b&oe=5EE66A9E",
 *                       "width": 410
 *                     },
 *                     {
 *                       "height": 320,
 *                       "source": "https://scontent-dus1-1.xx.fbcdn.net/v/t1.0-0/p320x320/98174169_101486681582348_7512797377309179904_n.png?_nc_cat=103&_nc_sid=85a577&_nc_ohc=HlIA8UQWDc4AX_zwgiY&_nc_ht=scontent-dus1-1.xx&oh=81756ca36a231553b0c6da470a25a390&oe=5EEA077D",
 *                       "width": 320
 *                     },
 *                     {
 *                       "height": 130,
 *                       "source": "https://scontent-dus1-1.xx.fbcdn.net/v/t1.0-0/p130x130/98174169_101486681582348_7512797377309179904_n.png?_nc_cat=103&_nc_sid=85a577&_nc_ohc=HlIA8UQWDc4AX_zwgiY&_nc_ht=scontent-dus1-1.xx&oh=7e9d7c52cf4e85fcf10ab8f7b9032781&oe=5EE67B14",
 *                       "width": 130
 *                     },
 *                     {
 *                       "height": 225,
 *                       "source": "https://scontent-dus1-1.xx.fbcdn.net/v/t1.0-0/p75x225/98174169_101486681582348_7512797377309179904_n.png?_nc_cat=103&_nc_sid=85a577&_nc_ohc=HlIA8UQWDc4AX_zwgiY&_nc_ht=scontent-dus1-1.xx&oh=b6acd22e770fc76635183e2689ddb830&oe=5EE72968",
 *                       "width": 225
 *                     }
 *                   ],
 *                   "created_time": "2020-05-18T12:04:40+0000",
 *                   "id": "101486678249015"
 *                 }
 *               ],
 *               "paging": {
 *                 "cursors": {
 *                   "before": "QVFIUmxkNGRmMDhFRFl1S2hJYVZAiZAUhBdjZAYeE5PRW41VGZAoZADNORFdiX3RaSUo0RlRxempuT2pkUjh2S29wclQyVmttbjl0cjFtUklfdDZAJZAXRPOEd3X3JR",
 *                   "after": "QVFIUmxkNGRmMDhFRFl1S2hJYVZAiZAUhBdjZAYeE5PRW41VGZAoZADNORFdiX3RaSUo0RlRxempuT2pkUjh2S29wclQyVmttbjl0cjFtUklfdDZAJZAXRPOEd3X3JR"
 *                 }
 *               }
 *             },
 *             "location": {
 *               "city": "Gütersloh",
 *               "country": "Germany",
 *               "latitude": 51.90209,
 *               "longitude": 8.38059,
 *               "street": "Neuenkirchener Strasse 13",
 *               "zip": "33332"
 *             },
 *             "access_token": "EAAwph5aXNvMBANXoeBCaq6pHZBp3KqZAcw49Fzm6T0LOhokB3hYOt8b04S8d9jDIjP2pTqSAPQMFTsA4yhGlPXX5cN3BWSOTCfp9Utni6bG2tEVp1ybRN7n7CE7DEJYKqouvp5u4N3VZBUsn4qea7fh5fIRkufC9SxTh9WXHAO2KDJrZCQHDi3186VsYeoud98ZD",
 *             "id": "101481864916163",
 *           }
 *         ],
 *         "paging": {
 *           "cursors": {
 *             "before": "QVFIUnY5ZAHZATNnhOWlZAQakpBajlvMGd5YkhDdUxhY2oyTHlfYkJvQmpxOTEwNVp1M1BTUVFkTVI2SFRVRjBHUTQzN0YZD",
 *             "after": "QVFIUmVwMTVaQjlqenJmVk9PdWF3WWRqWnBGWk5nUFUyT25LWkhjb0pRN3htUzR0TW5iZAFZAFTU5WTUR1b3ljeUQyMV8ZD"
 *           }
 *         }
 *       },
 *       "access_token": "EAAwph5aXNvMBAKSh3tqx8fLUWYWhoIBGy88wZBqx9NOOV8UZCqhmIkWXIOa7xE4TbEa5Re1tFx6l42MSGSpndmcCwZBgZCxiuJesVcPM0R3P9zsOlA9Sa5PDoZCSIrxwJPMckqyDHoPqE1TF87qaYMufoJkhs4eq43cMwfEmI10d6D82Dy08RtqgKe97Km4EcrQ04Kq4bhcZD",
 *       "id": "108867494164437"
 *       "instagram_business_account": {
 *         "id": "17841434570027727"
 *       }
 *     }
 *   ],
 *   "paging": {
 *     "cursors": {
 *       "before": "MTA4ODY3NDk0MTY0NDM3",
 *       "after": "MTA4ODY3NDk0MTY0NDM3"
 *     }
 *   }
 * }
 */
function getFacebookUserManagedPages() {
  const pagesPromise = new Promise((mainResolve, mainReject) => {
    const managedPagesDetails = [];

    // Get initial simple list of managed pages.
    const pagesListUrl =
      '/me/accounts?fields=id,access_token,name,has_transitioned_to_new_page_experience';
    FB.api(pagesListUrl, (listResponse) => {
      console.log('[DEBUG] Managed pages list:', listResponse);

      if (!listResponse.data || listResponse.error) {
        console.error(
          '[ERROR] Failed to fetch managed pages list:',
          listResponse
        );
        Sentry.captureMessage('Failed to fetch managed pages list', {
          extra: {
            response: JSON.stringify({error: listResponse.error}),
          },
          level: 'error',
        });
        return mainReject(listResponse.error.message);
      }

      // For each make a details-API call that differs whether it's an
      // NPE (new page experience) page or not, e.g. skip locations
      // field for NPE pages.
      const detailedPagesPromises = [];
      for (const page of listResponse.data) {
        let pageDetailUrl =
          '/' +
          page.id +
          '?fields=id,access_token,name,link,' +
          'photos{images,created_time},' +
          'instagram_business_account,has_transitioned_to_new_page_experience';
        if (!page.has_transitioned_to_new_page_experience) {
          pageDetailUrl +=
            ',locations{id,access_token,name,link,' +
            'photos{images,created_time},location}';
        }
        console.log('[DEBUG] Detail Page URL: ' + pageDetailUrl);
        const detailPromise = new Promise((detailResolve, detailReject) => {
          FB.api(pageDetailUrl, (pageDetails) => {
            console.log('[DEBUG] Detail Page', pageDetails);
            detailResolve(pageDetails);
          });
        });
        detailedPagesPromises.push(detailPromise);
      }

      Promise.all(detailedPagesPromises).then((detailedPages) => {
        console.log('[DEBUG] Detailed Pages:', detailedPages);

        // Note: The stringified messages tends to get too long for
        //   Sentry, which will heavily truncate it. Only keep most
        //   critical fields for debugging.
        const shortenedPagesForLogging = [];
        for (let page of detailedPages) {
          const shortPage = {
            name: page.name,
            link: page.link,
            linked_instagram_page_id: page.instagram_business_account
              ? page.instagram_business_account.id
              : null,
            has_transitioned_to_new_page_experience:
              page.has_transitioned_to_new_page_experience,
          };
          shortenedPagesForLogging.push(shortPage);
        }
        Sentry.captureMessage('Fetched facebook managed_pages', {
          extra: {
            pages: shortenedPagesForLogging,
          },
          level: 'info',
        });

        mainResolve(detailedPages);
      });
    });
  });

  return pagesPromise;
}

function logoutFacebook() {
  console.log('[DEBUG] Forcing Facebook logout...');
  if (FB) {
    FB.logout(); // This will kill the existing shortlived user access token.
  }
  sessionStorage.removeItem(FACEBOOK_SESSION_LOGGED_IN_KEY);
}

export default {
  getFacebookUserAccessToken: getFacebookUserAccessToken,
  getFacebookUserManagedPages: getFacebookUserManagedPages,
  getFacebookUserName: getFacebookUserName,
  logoutFacebook: logoutFacebook,
};



// WEBPACK FOOTER //
// ./src/facebookApi.js